import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
} from '@mui/material';
import { FC, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { NewConvergeButton } from '../../../../presentation/components/new-button';
import {
  ConvergeCheckbox,
  UncontrolledNewFormNumberInputAdornmentField,
} from '../../../../presentation';
import { useConvergeSnackBar } from '../../../../contexts';
import { useUpsertSubmissionMutation } from '@converge/graphql';
import './capacity-provider-allocation-modal.scss';

interface IQuotaShare {
  uuid: string | undefined;
  name: string | null | undefined;
  quotaShare: number;
}

interface CapacityProviderAllocationModalProps {
  open: boolean;
  onClose: () => void;
  quotaShare: IQuotaShare[];
  submissionUuid: string;
}

export const CapacityProviderAllocationModal: FC<
  CapacityProviderAllocationModalProps
> = ({ open, onClose, quotaShare, submissionUuid }) => {
  const { notify, closeSnackbar } = useConvergeSnackBar();
  const { upsertSubmission } = useUpsertSubmissionMutation();
  const [selectedProviders, setSelectedProviders] = useState<string[]>(() => {
    // Initialize all providers as selected
    return quotaShare
      .map((provider) => provider.uuid || '')
      .filter((uuid) => uuid);
  });

  const [allocationValues, setAllocationValues] = useState<
    Record<string, number>
  >(() => {
    const initialValues: Record<string, number> = {};
    quotaShare.forEach((provider) => {
      if (provider.uuid) {
        initialValues[provider.uuid] = +provider.quotaShare * 100;
      }
    });
    return initialValues;
  });

  const total = Object.values(allocationValues).reduce(
    (sum, value) => sum + value,
    0
  );

  const handleDebouncedAutoSave = useMemo(
    () => async () => {
      // Create capacity quota share array from allocationValues
      const capacityQuotaShare: Array<{
        quotaShare: number;
        capacityProviderUuid: string;
      }> = [];

      quotaShare.forEach((provider) => {
        if (provider.uuid) {
          capacityQuotaShare.push({
            quotaShare: (+allocationValues[provider.uuid] || 0) / 100, // Convert percentage to decimal
            capacityProviderUuid: provider.uuid,
          });
        }
      });

      const currentCapacity = {
        uuid: submissionUuid,
        capacityQuotaShare: capacityQuotaShare,
      };

      notify('Saving...', { variant: 'info' });

      console.log('currentCapacity', currentCapacity);

      try {
        await upsertSubmission({
          variables: { details: currentCapacity },
        });
        closeSnackbar();
        notify('Saved successfully', { variant: 'success' });
        onClose();
      } catch (e) {
        closeSnackbar();
        notify(
          `Error saving: ${e instanceof Error ? e.message : 'Unknown error'}`,
          { variant: 'error' }
        );
      }
    },
    [
      onClose,
      notify,
      closeSnackbar,
      upsertSubmission,
      allocationValues,
      quotaShare,
      submissionUuid,
    ]
  );

  const handleCheckboxChange = (providerUuid: string) => {
    setSelectedProviders((prev) => {
      const isCurrentlySelected = prev.includes(providerUuid);
      const newSelected = isCurrentlySelected
        ? prev.filter((id) => id !== providerUuid)
        : [...prev, providerUuid];

      setAllocationValues((prevValues) => ({
        ...prevValues,
        [providerUuid]: isCurrentlySelected ? 0 : 0,
      }));

      return newSelected;
    });
  };

  const handleAllocationChange = (providerUuid: string, value: string) => {
    const numericValue = parseFloat(value) || 0;
    setAllocationValues((prev) => ({
      ...prev,
      [providerUuid]: numericValue,
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{ '& .MuiDialog-paper': { width: '600px' } }}
    >
      <DialogTitle sx={{ padding: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          paddingTop="4px"
          paddingRight="15px"
        >
          <Box className="capacity-title-modal">
            Edit Capacity Provider Allocation
          </Box>

          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Box className="context-style">
          You can change allocation percentages and select any combination of
          capacity providers needed. Please remember that the total must equal
          100%.{' '}
        </Box>
        <Box className="main-container-provider-modal">
          {Array.isArray(quotaShare) &&
            quotaShare.map((provider) => {
              return (
                <>
                  <Box className="each-row">
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                      }}
                    >
                      <ConvergeCheckbox
                        checked={selectedProviders.includes(
                          provider.uuid || ''
                        )}
                        onChange={(checked) =>
                          handleCheckboxChange(provider.uuid || '')
                        }
                      />
                      {provider.name}
                    </div>
                    <Box
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                      }}
                    >
                      <UncontrolledNewFormNumberInputAdornmentField
                        dataTestid={`allocation-${provider.uuid}-textfield`}
                        adornmentText="%"
                        containerWidth={2}
                        value={allocationValues[provider.uuid || ''] || 0}
                        name={`allocation-${provider.uuid}`}
                        setGbeQuotaShareValue={(value) =>
                          handleAllocationChange(
                            provider.uuid || '',
                            value.toString()
                          )
                        }
                        setCanopiusShareValue={(value) => {}} // Not used in our case
                        handleDebouncedAutoSave={() => {}}
                        isCheckBox={
                          !selectedProviders.includes(provider.uuid || '')
                        }
                      />
                    </Box>
                  </Box>
                </>
              );
            })}
          <Box className="each-row">
            <Box></Box>
            <Box
              className="total-provider-modal"
              style={{ color: +total === 100 ? 'black' : 'red' }}
            >
              {' '}
              {total}%
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 3,
            gap: 2,
          }}
        >
          <NewConvergeButton
            variant="outlined"
            label="Cancel"
            onClick={onClose}
          />
          <Box style={{ marginRight: '15px', marginBottom: '20px' }}>
            <NewConvergeButton
              disabled={+total !== 100}
              variant="contained"
              label="UPDATE"
              onClick={handleDebouncedAutoSave}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
